import React, { useRef, useState, useEffect } from 'react';
import Tools from '../tools';
import { fabric } from '../../../../../vendor/fabric';
import { Modal, Input } from 'antd';

import styles from './style.module.css';
import { handleRect } from '../handleRect';
import { handleArrow } from '../handleArrow';
import { getCanvasSize } from '../getCanvasSize';
import { buildCanvas } from '../buildCanvas';

type Props = {
  blob?: string;
  onSave: (dataURL: string) => void;
  onCancel: () => void;
  toolsFixed?: boolean;
  containerWidth?: number;
  containerHeight?: number;
  container?: HTMLElement;
};

export enum Tool {
  Mouse = 'mouse',
  Arrow = 'arrow',
  Rect = 'rect',
  Draw = 'draw',
  Text = 'text',
  Trash = 'trash'
}

export const AnnotateImage = ({
  blob,
  onSave,
  onCancel,
  toolsFixed = false,
  containerWidth,
  containerHeight,
  container = document.body
}: Props) => {
  const imageRef = useRef(null);
  const [color, setColor] = useState<string>('#FCB900');
  const [textActive, setTextActive] = useState<boolean>(false);
  const [text, setText] = useState<string>('');
  const [tool, setTool] = useState<Tool>(Tool.Draw);
  const [selectedObject, setSelectedObject] = useState<any>(undefined);
  const [saved, setSaved] = useState<boolean>(false);
  const canvasRef = useRef<any>(null);

  useEffect(() => {
    if (blob) {
      buildCanvas({
        blob,
        imageRef,
        canvasRef,
        color,
        containerWidth,
        containerHeight,
        setSelectedObject
      });
    }
  }, []);

  const resetToolToMouse = () => setTool(Tool.Mouse);

  const handleToolChange = (tool: Tool) => {
    switch (tool) {
      case Tool.Mouse:
        if (canvasRef.current) canvasRef.current.isDrawingMode = false;
        break;
      case Tool.Arrow:
        handleArrow({ canvasRef, color, callback: resetToolToMouse });
        break;
      case Tool.Rect:
        handleRect({ canvasRef, color, callback: resetToolToMouse });
        break;
      case Tool.Draw:
        if (canvasRef.current) canvasRef.current.isDrawingMode = true;
        break;
      case Tool.Text:
        setTextActive(true);
        break;
    }
  };

  useEffect(() => {
    handleToolChange(tool);
  }, [tool]);

  const resetText = () => {
    setText('');
    setTextActive(false);
  };

  const renderText = () => {
    const object = new fabric.Text(text, {
      fontFamily: 'Arial',
      fill: color
    });
    canvasRef.current
      .add(object)
      .centerObject(object)
      .setActiveObject(object);
    resetToolToMouse();

    setText('');
  };

  const handleOk = async () => {
    if (!blob) return console.error('No image blob provided');

    const dimensions = await getCanvasSize({
      blob,
      containerWidth,
      containerHeight
    });
    const dataURL = canvasRef.current.toDataURL({
      format: 'png',
      multiplier: 1 / dimensions.scale
    });
    setSaved(true);
    onSave(dataURL);
  };

  const handleCancel = () => {
    if (!saved) {
      canvasRef.current.remove(...canvasRef.current.getObjects());
    }
    onCancel();
  };

  const handleDelete = () => {
    canvasRef.current.remove(canvasRef.current.getActiveObject());
  };

  const handleColorChange = color => {
    setColor(color);
    canvasRef.current.freeDrawingBrush.color = color;
    const activeObject = canvasRef.current.getActiveObject();
    if (!activeObject) return;

    if (activeObject.stroke) activeObject.set({ stroke: color });
    if (activeObject.fill) activeObject.set({ fill: color });
    canvasRef.current.renderAll();
  };

  return (
    <div className={styles.container}>
      <div className={styles.markerContainer}>
        <canvas className={styles.image} ref={imageRef} />
      </div>
      <Tools
        color={color}
        tool={tool}
        setTool={setTool}
        onSave={handleOk}
        onCancel={handleCancel}
        onDelete={handleDelete}
        canDelete={!!selectedObject}
        container={container}
        onColorChange={handleColorChange}
        fixedToWindow={toolsFixed}
      />
      <Modal
        open={textActive}
        getContainer={container}
        destroyOnClose
        title="Text Annotation"
        okText="Add"
        maskClosable
        onOk={() => {
          renderText();
          resetText();
        }}
        okButtonProps={{ disabled: !text }}
        onCancel={resetText}
      >
        <form
          onSubmit={e => {
            e.preventDefault();
            if (!text) return;
            renderText();
            resetText();
          }}
        >
          <Input
            autoFocus
            ref={inputRef => inputRef && inputRef.focus()}
            size="large"
            type="text"
            placeholder="Enter your text"
            onChange={e => setText(e.target.value)}
          />
        </form>
      </Modal>
    </div>
  );
};
